import { useEffect, useState } from 'react'
import BottomSheetContent from '../BottomSheetContent'
import RenderHTML from '../../ui-components/RenderHTML'
import RiveComponent, { RiveFit } from '../../RiveComponent'
import { PrimaryButton } from '../../execution/PrimaryButton'

type KYCCompletionBottomSheetProps = {
  onDismiss: () => void
  onAuthorize: () => void
  familyMemberName: string
  consentText: string
}

enum KYCStepId {
  AADHAAR_KYC = 'Aadhaar KYC',
  AADHAAR_ESIGN = 'Aadhaar eSign',
  PAYMENT = 'Payment'
}

type KYCStep = {
  id: KYCStepId
  description: string
  riveSrc: string
  waitForBeforePlaying: number // in milliseconds
}

const kycSteps: KYCStep[] = [
  {
    id: KYCStepId.AADHAAR_KYC,
    description: 'Aadhaar KYC',
    riveSrc: '/rives/family/aadhaar-step.riv',
    waitForBeforePlaying: 0
  },
  {
    id: KYCStepId.PAYMENT,
    description: 'Payment',
    riveSrc: '/rives/family/payment-step.riv',
    waitForBeforePlaying: 350
  },
  {
    id: KYCStepId.AADHAAR_ESIGN,
    description: 'Aadhaar eSign',
    riveSrc: '/rives/family/esign-step.riv',
    waitForBeforePlaying: 900
  }
]

export const KYCCompletionBottomSheet = ({
  onDismiss,
  onAuthorize,
  familyMemberName,
  consentText
}: KYCCompletionBottomSheetProps) => {
  const [isConsentChecked, setIsConsentChecked] = useState(true)
  const [rivesCanPlay, setRivesCanPlay] = useState<{ [x in KYCStepId]: boolean }>({
    [KYCStepId.AADHAAR_KYC]: false,
    [KYCStepId.AADHAAR_ESIGN]: false,
    [KYCStepId.PAYMENT]: false
  })

  const handleAuthorize = () => {
    if (isConsentChecked) {
      onAuthorize()
    }
  }

  return (
    <BottomSheetContent
      headingComponent={
        <div className='text-[#232323] text-[22px] leading-[130%] px-5 pb-7'>
          <span className='font-[700]'> {familyMemberName} needs to complete </span> <br /> their KYC from this phone
        </div>
      }
      onDismiss={onDismiss}
      bottomSheetHeadClassName='!pt-5'
      bottomSheetBodyClassName='!px-0 !pb-1'
      bottomSheetCtas={
        <PrimaryButton
          onClick={handleAuthorize}
          label='Authorize with OTP'
          isDisabled={!isConsentChecked}
          primaryButtonClassName='py-4 px-4 text-[16px] leading-[130%] !h-[56px]'
        />
      }
    >
      <div className='flex flex-col'>
        {/* KYC Steps */}
        <div className='flex flex-col gap-[18px] pb-2'>
          {kycSteps.map((step, index) => (
            <KYCStep
              key={`kyc-step-${index}-${step.id}`}
              stepNumber={index + 1}
              description={step.description}
              riveSrc={step.riveSrc}
              isLast={index === kycSteps.length - 1}
              riveCanPlay={rivesCanPlay[step.id]}
              waitForBeforePlaying={step.waitForBeforePlaying}
              onCanPlay={() => setRivesCanPlay((prev) => ({ ...prev, [step.id]: true }))}
            />
          ))}
        </div>

        {/* Consent Checkbox */}
        <div
          onClick={() => setIsConsentChecked(!isConsentChecked)}
          className='flex items-start gap-3 cursor-pointer pl-6 pr-5 border-t border-[#EBF2F2] pt-5'
        >
          <div className='mt-1 flex-shrink-0'>
            <RenderHTML innerHTMLText={isConsentChecked ? CheckedBox : UncheckedBox} />
          </div>
          <div className='flex-1 text-[#8E9393] text-[12px] font-[400] leading-[150%] max-w-[90%]'>{consentText}</div>
        </div>
      </div>
    </BottomSheetContent>
  )
}

const UncheckedBox = `<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
  <rect x="0.75" y="0.75" width="18.5" height="18.5" rx="5.25" stroke="#8E9393" stroke-width="1.5"/>
</svg>`

const CheckedBox = `<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
  <rect width="20" height="20" rx="6" fill="#3D3D3D"/>
  <path d="M5.41504 10.4199L7.9958 12.6781C8.41143 13.0418 9.0432 12.9996 9.40688 12.584L14.5817 6.66992" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
</svg>`

type KYCStepProps = {
  stepNumber: number
  description: string
  riveSrc: string
  isLast?: boolean
  riveCanPlay: boolean
  waitForBeforePlaying: number
  onCanPlay: () => void
}

const KYCStep = ({
  stepNumber,
  description,
  riveSrc,
  isLast = false,
  riveCanPlay,
  waitForBeforePlaying,
  onCanPlay
}: KYCStepProps) => {
  useEffect(() => {
    const timer = setTimeout(onCanPlay, waitForBeforePlaying)
    return () => clearTimeout(timer)
  }, [waitForBeforePlaying])

  return (
    <div className='flex items-center gap-3 pl-5'>
      <div className='flex items-center justify-center w-10 h-10 rounded-xl flex-shrink-0 -mt-4'>
        {riveCanPlay && <RiveComponent src={riveSrc} fit={RiveFit.CONTAIN} className='w-full h-full' />}
      </div>
      <div className={`flex flex-col flex-1 border-b border-[#EBF2F2] pb-4 pr-5 ${isLast ? 'border-b-0' : ''}`}>
        <div className='text-[#8E9393] text-[12px] font-[400] leading-[130%]'>Step {stepNumber}</div>
        <div className='text-[#232323] text-[14px] font-[500] leading-[130%]'>{description}</div>
      </div>
    </div>
  )
}
